import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  generateMonthSummary,
  updateMonthSummary,
} from "../../services/analyticsService";
import { Sparkles, Eye, Copy, Check } from "lucide-react";
import { noteMarkdownComponents } from "../../utils/noteMarkdownComponents";
import NoteMarkdownEditor from "../ui/NoteMarkdownEditor";

function cleanCurrency(s) {
  if (!s) return "—";
  return s.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

function getBadgeClass(text) {
  if (!text) return "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]";
  if (String(text).includes("arriving")) {
    return "bg-[var(--warn-soft,#fbf1e0)] text-[var(--warn,#b8791e)]";
  }
  if (text.includes("Over")) return "bg-[var(--crit-soft,#fbe6e6)] text-[var(--crit,#c0262b)]";
  if (text.includes("Under")) return "bg-[var(--good-soft,#e8f3ea)] text-[var(--good,#1f6b34)]";
  if (text.includes("Closed")) return "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]";
  if (text.includes("Open")) return "bg-[var(--band,#eef3fa)] text-[#3b6ea8]";
  return "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]";
}

function varianceClass(text) {
  const s = String(text || "");
  if (s.includes("Over") || s.startsWith("+") || /over/i.test(s)) {
    return "text-[var(--crit,#c0262b)]";
  }
  if (s.includes("Under") || s.startsWith("−") || s.startsWith("-") || /under/i.test(s)) {
    return "text-[var(--good,#1f6b34)]";
  }
  return "text-[var(--ink,#1c1c1a)]";
}

function noteStatusLabel(status) {
  if (!status) return null;
  if (status === "completed") return "Ready";
  if (status === "pending" || status === "processing") return "Generating…";
  if (status === "empty") return "No notes";
  if (status === "failed") return "Failed";
  return status;
}

const Report20MonthlyFinancialLedger = ({
  ledger,
  year,
  onRefresh,
  monthSummaryEnabled = true,
  canEdit = false,
  exportRef,
}) => {
  const [noteModal, setNoteModal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingMonth, setGeneratingMonth] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [noteCopied, setNoteCopied] = useState(false);
  const noteContentRef = useRef(null);

  const showMonthlyNote = Boolean(monthSummaryEnabled);

  const rows = useMemo(() => {
    if (!ledger || !Array.isArray(ledger)) return [];
    return ledger;
  }, [ledger]);

  const hasInFlightNotes = useMemo(
    () =>
      showMonthlyNote &&
      rows.some(
        (r) =>
          r.monthly_note_status === "pending" ||
          r.monthly_note_status === "processing",
      ),
    [rows, showMonthlyNote],
  );

  useEffect(() => {
    if (!hasInFlightNotes || typeof onRefresh !== "function") return undefined;
    const id = setInterval(() => {
      onRefresh();
    }, 10000);
    return () => clearInterval(id);
  }, [hasInFlightNotes, onRefresh]);

  const closeNoteModal = () => {
    setNoteModal(null);
    setIsEditing(false);
    setDraftNote("");
    setModalError(null);
    setSavingNote(false);
    setRegenerating(false);
    setNoteCopied(false);
  };

  const openNoteModal = (row) => {
    setNoteModal({
      month: row.month,
      month_num: Number(row.month_num),
      note: row.monthly_note || "",
      status: row.monthly_note_status,
    });
    setIsEditing(false);
    setDraftNote(row.monthly_note || "");
    setModalError(null);
    setNoteCopied(false);
  };

  const handleCopyMonthlyNote = useCallback(async () => {
    const body =
      noteContentRef.current?.innerText?.trim() ||
      String(noteModal?.note || "").trim();
    if (!body) {
      setModalError("Nothing to copy");
      return;
    }
    const text = ["Monthly note", noteModal?.month || "", "", body].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setNoteCopied(true);
      setModalError(null);
      window.setTimeout(() => setNoteCopied(false), 2000);
    } catch {
      setModalError("Failed to copy note");
    }
  }, [noteModal]);

  const escapeCsv = (value) => {
    if (value == null) return "";
    const str = String(value);
    const escaped = str.replace(/"/g, '""');
    if (/[",\n]/.test(str)) {
      return `"${escaped}"`;
    }
    return escaped;
  };

  const exportCsv = useCallback(() => {
    if (!rows.length) return;

    const headers = [
      "Month",
      "Status",
      ...(showMonthlyNote ? ["Monthly Note"] : []),
      "Monthly Budget",
      "Monthly Actual",
      "Monthly Variance",
      "Cumulative Budget",
      "Cumulative Actual",
      "Cumulative Variance",
    ];

    const csvRows = [
      headers.map(escapeCsv).join(","),
      ...rows.map((r) =>
        [
          r.month,
          r.status,
          ...(showMonthlyNote
            ? [r.monthly_note || noteStatusLabel(r.monthly_note_status) || ""]
            : []),
          r.monthly_budget,
          r.monthly_actual,
          r.monthly_variance,
          r.cumulative_budget || "",
          r.cumulative_actual || "",
          r.cumulative_variance || "",
        ]
          .map(escapeCsv)
          .join(",")
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob(["\uFEFF" + csvString], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "monthly-ledger.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, showMonthlyNote]);

  useEffect(() => {
    if (!exportRef) return undefined;
    exportRef.current = exportCsv;
    return () => {
      exportRef.current = null;
    };
  }, [exportRef, exportCsv]);

  const handleGenerate = async (row) => {
    const monthNum = Number(row.month_num);
    if (!year || !monthNum) return;
    setGenerateError(null);
    setGeneratingMonth(monthNum);
    try {
      await generateMonthSummary(year, monthNum);
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (err) {
      setGenerateError(err.message || "Failed to generate monthly note");
    } finally {
      setGeneratingMonth(null);
    }
  };

  const handleSaveNote = async () => {
    if (!noteModal?.month_num || !year) return;
    setSavingNote(true);
    setModalError(null);
    try {
      const res = await updateMonthSummary(year, noteModal.month_num, draftNote);
      const savedText = res?.summary?.summary_text ?? draftNote;
      setNoteModal((prev) =>
        prev
          ? { ...prev, note: savedText, status: "completed" }
          : prev,
      );
      setIsEditing(false);
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (err) {
      setModalError(err.message || "Failed to save monthly note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleRegenerate = async () => {
    if (!noteModal?.month_num || !year) return;
    setRegenerating(true);
    setModalError(null);
    try {
      await generateMonthSummary(year, noteModal.month_num);
      closeNoteModal();
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (err) {
      setModalError(err.message || "Failed to regenerate monthly note");
    } finally {
      setRegenerating(false);
    }
  };

  const renderMonthlyNoteCell = (r) => {
    const status = r.monthly_note_status;
    const isClosed = r.status === "Closed";
    const isBusy = status === "pending" || status === "processing";
    const hasNote = status === "completed" && r.monthly_note;
    const monthBusy = generatingMonth === Number(r.month_num);

    if (hasNote) {
      return (
        <button
          type="button"
          onClick={() => openNoteModal(r)}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--accent,#a51c1c)] hover:underline"
          >
          <Eye className="h-3.5 w-3.5" />
          View note
        </button>
      );
    }

    if (isBusy) {
      return (
        <span className="text-xs font-medium text-amber-700">Generating…</span>
      );
    }

    if (status === "empty") {
      return (
        <div className="flex flex-col gap-1 items-start">
          <span className="text-[12px] text-[var(--muted,#6b6a66)]">No note yet</span>
          {isClosed && year && canEdit && (
            <button
              type="button"
              disabled={monthBusy}
              onClick={() => handleGenerate(r)}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 py-1 text-[12px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" />
              {monthBusy ? "Generating…" : "Generate"}
            </button>
          )}
        </div>
      );
    }

    if (status === "failed") {
      return (
        <div className="flex flex-col gap-1 items-start">
          <span className="text-xs font-medium text-red-600">Failed</span>
          {isClosed && year && canEdit && (
            <button
              type="button"
              disabled={monthBusy}
              onClick={() => handleGenerate(r)}
              className="text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50"
            >
              {monthBusy ? "Queuing…" : "Retry"}
            </button>
          )}
        </div>
      );
    }

    if (isClosed && year && canEdit) {
      return (
        <button
          type="button"
          disabled={monthBusy}
          onClick={() => handleGenerate(r)}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 py-1 text-[12px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
          {monthBusy ? "Queuing…" : "Generate"}
        </button>
      );
    }

    if (isClosed && year && !canEdit) {
      return <span className="text-xs text-slate-400">—</span>;
    }

    return <span className="text-xs text-slate-400">—</span>;
  };

  return (
    <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
      <div className="flex justify-between items-start px-5 py-4">
        <div>
          <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">
            Monthly financial ledger
          </h3>
          <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
            Closed months are final. Open months show invoices received so far.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-[var(--muted,#6b6a66)]">Included in Export</span>
          {!exportRef && (
            <button
              onClick={exportCsv}
              disabled={!rows.length}
              className="inline-flex items-center justify-center rounded-md bg-[#a51c1c] px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-40"
            >
              Export
            </button>
          )}
        </div>
      </div>

      {generateError && (
        <div className="px-5 py-2 text-xs text-red-600 border-b border-red-50 bg-red-50">
          {generateError}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--line,#e4e2dc)] bg-[var(--sunken2,#fbfaf8)]">
              <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Month
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Status
              </th>
              {showMonthlyNote && (
                <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Monthly note
                </th>
              )}
              <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Budget
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Actual
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Variance
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Cum. budget
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Cum. actual
              </th>
              <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Cum. variance
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => {
              const isCurrentMonth =
                Number(year) === new Date().getFullYear() &&
                Number(r.month_num) === new Date().getMonth() + 1;
              return (
              <tr
                key={i}
                className={`border-b border-[var(--line,#e4e2dc)] last:border-0 ${
                  isCurrentMonth ? "bg-[var(--band,#eef3fa)]" : "bg-white"
                }`}
              >
                <td className="px-4 py-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                  {r.month}
                </td>

                <td className="px-4 py-3 text-sm">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-[12px] font-medium ${getBadgeClass(
                      r.status,
                    )}`}
                  >
                    {r.status}
                  </span>
                </td>

                {showMonthlyNote && (
                  <td className="px-3 py-3 text-sm">{renderMonthlyNoteCell(r)}</td>
                )}

                <td className="px-3 py-3 text-[13px] text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {r.monthly_budget}
                </td>

                <td className="px-3 py-3 text-[13px] text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {r.monthly_actual}
                </td>

                <td className={`px-3 py-3 text-[13px] text-right tabular-nums ${varianceClass(r.monthly_variance)}`}>
                  {cleanCurrency(r.monthly_variance)}
                </td>

                <td className="px-3 py-3 text-[13px] text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {r.cumulative_budget || "—"}
                </td>

                <td className="px-3 py-3 text-[13px] text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {r.cumulative_actual || "—"}
                </td>

                <td className={`px-4 py-3 text-[13px] text-right tabular-nums ${varianceClass(r.cumulative_variance)}`}>
                  {cleanCurrency(r.cumulative_variance) || "—"}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {!rows.length && (
          <div className="text-center py-6 text-sm text-slate-500">
            No data available
          </div>
        )}
      </div>

      {noteModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          role="presentation"
          onClick={closeNoteModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="month-note-title"
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] shadow-xl border border-gray-200 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex-shrink-0 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2
                  id="month-note-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  Monthly note
                </h2>
                <p className="text-xs text-gray-500 mt-1">{noteModal.month}</p>
              </div>
              {!isEditing && (
                <button
                  type="button"
                  onClick={handleCopyMonthlyNote}
                  disabled={savingNote || regenerating}
                  className="inline-flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Copy note to clipboard"
                >
                  {noteCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      Copy
                    </>
                  )}
                </button>
              )}
            </div>

            {modalError && (
              <div className="px-5 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">
                {modalError}
              </div>
            )}

            <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
              {isEditing ? (
                <NoteMarkdownEditor
                  value={draftNote}
                  onChange={setDraftNote}
                  disabled={savingNote || regenerating}
                  minHeightClass="min-h-[280px]"
                  className="max-h-[50vh]"
                  aria-label="Monthly note"
                  placeholder="Monthly note"
                />
              ) : (
                <div
                  ref={noteContentRef}
                  className="text-sm text-gray-700"
                >
                  <ReactMarkdown components={noteMarkdownComponents}>
                    {noteModal.note || ""}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            <div className="px-5 pb-4 pt-2 border-t border-gray-100 flex flex-wrap justify-end gap-2 flex-shrink-0">
              {canEdit && !isEditing && (
                <>
                  <button
                    type="button"
                    disabled={regenerating || savingNote}
                    onClick={handleRegenerate}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {regenerating ? "Regenerating…" : "Regenerate"}
                  </button>
                  <button
                    type="button"
                    disabled={regenerating}
                    onClick={() => {
                      setDraftNote(noteModal.note || "");
                      setIsEditing(true);
                      setModalError(null);
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50"
                  >
                    Edit
                  </button>
                </>
              )}

              {canEdit && isEditing && (
                <>
                  <button
                    type="button"
                    disabled={savingNote}
                    onClick={() => {
                      setIsEditing(false);
                      setDraftNote(noteModal.note || "");
                      setModalError(null);
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={savingNote}
                    onClick={handleSaveNote}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingNote ? "Saving…" : "Save"}
                  </button>
                </>
              )}

              <button
                type="button"
                disabled={savingNote || regenerating}
                onClick={closeNoteModal}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report20MonthlyFinancialLedger;
